/**
 * Accounting Module - Execution Page
 * Unified master-detail layout: generation controls in header,
 * batch history table at top, side-by-side events + entries grids below.
 * Note: Chart of Accounts and Rules configuration moved to Admin > Accounting tab
 * Note: Valuations tab moved to the dedicated Valuation page
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { useAuth } from '../../components/auth/AuthContext';
import ResizeHandle from '../../components/common/ResizeHandle';
import { accountingService } from '../../services/api/accountingService';
import { AccountingEvent, AccountingEntry, AccountingBatch, AccountingConcept, AccountingRule, CONCEPT_LABELS, GenerationResult, BatchRunReport } from '../../types/accounting';
import Toast from '../../components/common/Toast';
import AlertModal from '../../components/common/AlertModal';
import TemplatePicker from '../../components/outputFormats/TemplatePicker';
import { History } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FilterChangedEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../components/grids/TradeGrid.css';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import { useSystemDate } from '../../hooks/useSystemDate';
import { useAiContextMenu } from '../../hooks/useAiContextMenu';
import InlineMenu from '../../components/common/InlineMenu';
import './Accounting.css';

const ALL_CONCEPTS: AccountingConcept[] = [
  'NEW_TRADE',
  'VALUATION',
  'VALUATION_REVERSAL',
  'EXPIRY',
  'MODIFICATION_CANCELLED',
  'UNWIND',
];

const BACKDATE_CONCEPTS: AccountingConcept[] = [
  'NEW_TRADE',
  'EXPIRY',
  'MODIFICATION_CANCELLED',
  'UNWIND',
];

const Accounting: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  const clientId = user?.organization?.id || '';
  const systemDateResult = useSystemDate(clientId || undefined);

  // Role-based access checks
  const canView = ['client_admin', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'].includes(user?.role || '');
  const canManage = ['client_admin', 'client_bo_user', 'client_fo_bo_user', 'client_acc_user'].includes(user?.role || '');

  // Demo mode

  // AG Grid refs (declared early — needed by useAiContextMenu below)
  const eventsGridRef = useRef<AgGridReact>(null);
  const entriesGridRef = useRef<AgGridReact>(null);
  const eventsContainerRef = useRef<HTMLDivElement>(null);
  const entriesContainerRef = useRef<HTMLDivElement>(null);

  // Toast & Alert state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [alert, setAlert] = useState<{ title: string; message: string; type: 'info' | 'warning' | 'error' } | null>(null);

  // AI Context Menu — Events grid
  const {
    showMenu: showEventMenu,
    menuPosition: eventMenuPosition,
    menuItems: eventMenuItems,
    closeMenu: closeEventMenu,
  } = useAiContextMenu({
    objectType: 'accountingEvent',
    gridRef: eventsGridRef,
    containerRef: eventsContainerRef,
    getPlaceholders: (row) => ({
      event_type: row.conceptLabel,
      fecha: (row.eventDate || '').split('-').reverse().join('/'),
      trade_id: row.tradeNumber,
    }),
    onCopied: () => setToast({ message: t('aiContext.copied', 'Pregunta copiada — pégala en tu asistente de IA.'), type: 'success' }),
    onError: () => setToast({ message: t('aiContext.copyFailed', 'No se pudo copiar al portapapeles.'), type: 'error' }),
  });

  // AI Context Menu — Entries grid
  const {
    showMenu: showEntryMenu,
    menuPosition: entryMenuPosition,
    menuItems: entryMenuItems,
    closeMenu: closeEntryMenu,
  } = useAiContextMenu({
    objectType: 'accountingEntry',
    gridRef: entriesGridRef,
    containerRef: entriesContainerRef,
    getPlaceholders: (row) => ({
      entry_id: row.id,
      trade_id: row.tradeNumber,
      event_type: row.conceptLabel,
      fecha: (row.entryDate || '').split('-').reverse().join('/'),
    }),
    onCopied: () => setToast({ message: t('aiContext.copied', 'Pregunta copiada — pégala en tu asistente de IA.'), type: 'success' }),
    onError: () => setToast({ message: t('aiContext.copyFailed', 'No se pudo copiar al portapapeles.'), type: 'error' }),
  });

  // ============================================
  // Generation Controls State
  // ============================================
  const [selectedConcepts, setSelectedConcepts] = useState<Set<AccountingConcept>>(new Set(ALL_CONCEPTS));
  const [regenerateMode, setRegenerateMode] = useState<'skip_processed' | 'delete_and_regenerate'>('skip_processed');
  const [generating, setGenerating] = useState(false);
  const [generatingPhase, setGeneratingPhase] = useState<'events' | 'entries' | null>(null);

  // ============================================
  // Batches State
  // ============================================
  const [batches, setBatches] = useState<AccountingBatch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  // ============================================
  // Events + Entries State (loaded by selected batch)
  // ============================================
  const [events, setEvents] = useState<AccountingEvent[]>([]);
  const [entries, setEntries] = useState<AccountingEntry[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());

  // Rules lookup (for displaying rule name on each entry)
  const [rules, setRules] = useState<AccountingRule[]>([]);

  // Template picker
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // ============================================
  // Batch date filter (pending = UI inputs, applied = used in query)
  // ============================================
  const [batchDateFrom, setBatchDateFrom] = useState('');
  const [batchDateTo, setBatchDateTo] = useState('');
  const [pendingBatchDateFrom, setPendingBatchDateFrom] = useState('');
  const [pendingBatchDateTo, setPendingBatchDateTo] = useState('');

  // ============================================
  // Delete state
  // ============================================
  const [deletingBatchId, setDeletingBatchId] = useState<string | null>(null);
  const [confirmDeleteBatch, setConfirmDeleteBatch] = useState<AccountingBatch | null>(null);

  // ============================================
  // Back-dated Generation Modal State
  // ============================================
  const [showBackdateModal, setShowBackdateModal] = useState(false);
  const [backdateDate, setBackdateDate] = useState('');
  const [backdateConcepts, setBackdateConcepts] = useState<Set<AccountingConcept>>(new Set(['NEW_TRADE', 'EXPIRY', 'MODIFICATION_CANCELLED', 'UNWIND']));
  const [backdateGenerating, setBackdateGenerating] = useState(false);
  const [backdateGeneratingPhase, setBackdateGeneratingPhase] = useState<'events' | 'entries' | null>(null);

  // ============================================
  // Data Loading
  // ============================================

  const loadBatches = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await accountingService.getBatches(
        clientId, 50,
        batchDateFrom || undefined,
        batchDateTo || undefined,
      );
      if (response.success && response.data) {
        // Sort by effective date (dateFrom) descending, then generatedAt descending as tiebreaker
        const sorted = [...response.data].sort((a, b) => {
          const dateA = a.dateFrom || '';
          const dateB = b.dateFrom || '';
          if (dateA !== dateB) return dateB.localeCompare(dateA);
          return (b.generatedAt || '').localeCompare(a.generatedAt || '');
        });
        setBatches(sorted);
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
    }
  }, [clientId, batchDateFrom, batchDateTo]);

  const loadEventsForBatch = useCallback(async (batchId: string) => {
    if (!clientId) return;
    setEventsLoading(true);
    try {
      const response = await accountingService.getEvents(clientId, { batchId });
      if (response.success && response.data) {
        setEvents(response.data);
      }
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setEventsLoading(false);
    }
  }, [clientId]);

  const loadEntriesForBatch = useCallback(async (batchId: string) => {
    if (!clientId) return;
    setEntriesLoading(true);
    try {
      const response = await accountingService.getEntries(clientId, { batchId });
      if (response.success && response.data) {
        setEntries(response.data);
      }
    } catch (err) {
      console.error('Failed to load entries:', err);
    } finally {
      setEntriesLoading(false);
    }
  }, [clientId]);

  const loadRules = useCallback(async () => {
    if (!clientId) return;
    try {
      const response = await accountingService.getRules(clientId, { includeInactive: true });
      if (response.success && response.data) {
        setRules(response.data);
      }
    } catch (err) {
      console.error('Failed to load rules for entries lookup:', err);
    }
  }, [clientId]);

  const rulesMap = useMemo(() => {
    const map: Record<string, AccountingRule> = {};
    for (const rule of rules) {
      map[rule.id] = rule;
    }
    return map;
  }, [rules]);

  // Load batches and rules on mount
  useEffect(() => {
    if (clientId) {
      loadBatches();
      loadRules();
    }
  }, [clientId, loadBatches, loadRules]);



  // Auto-select the most recent batch on first load
  const initialBatchSelected = useRef(false);
  useEffect(() => {
    if (batches.length > 0 && !initialBatchSelected.current) {
      initialBatchSelected.current = true;
      setSelectedBatchId(batches[0].id);
    }
  }, [batches]);

  // Load events + entries when selected batch changes
  useEffect(() => {
    setSelectedEventIds(new Set());
    if (selectedBatchId) {
      loadEventsForBatch(selectedBatchId);
      loadEntriesForBatch(selectedBatchId);
    } else {
      setEvents([]);
      setEntries([]);
    }
  }, [selectedBatchId, loadEventsForBatch, loadEntriesForBatch]);

  // ============================================
  // Combined Generate Handler
  // ============================================

  const handleGenerate = async () => {
    if (!clientId || generating) return;

    if (selectedConcepts.size === 0) {
      setToast({ message: t('accounting.generate.noConceptsSelected', 'Select at least one concept'), type: 'error' });
      return;
    }

    setGenerating(true);
    setGeneratingPhase('events');

    const execDate = systemDateResult.systemDate || undefined;
    let eventsGenerated = 0;
    let entriesGenerated = 0;
    let batchId: string | null = null;

    try {
      // Step 1: Generate events
      const eventsResponse = await accountingService.generate(clientId, {
        dateFrom: execDate,
        dateTo: execDate,
        concepts: Array.from(selectedConcepts),
        regenerateMode,
      });

      if (eventsResponse.success && eventsResponse.data) {
        eventsGenerated = eventsResponse.data.eventsGenerated;
        batchId = eventsResponse.data.batchId;
      } else {
        setToast({ message: eventsResponse.message || t('accounting.generate.failed', 'Generation failed'), type: 'error' });
        setGenerating(false);
        setGeneratingPhase(null);
        return;
      }

      // Step 2: Generate entries from the new batch
      setGeneratingPhase('entries');

      try {
        const entriesResponse = await accountingService.generateEntries(clientId, {
          batchId: batchId || undefined,
          concepts: Array.from(selectedConcepts),
          dateFrom: execDate,
          dateTo: execDate,
        });

        if (entriesResponse.data) {
          entriesGenerated = entriesResponse.data.entriesGenerated;
        }
      } catch (entryErr) {
        // Events succeeded but entries failed — still show result
        console.error('Entry generation failed:', entryErr);
      }

      // Show combined result
      setToast({
        message: t('accounting.generate.combinedSuccess', 'Generated {{events}} events and {{entries}} entries', {
          events: eventsGenerated,
          entries: entriesGenerated,
        }),
        type: 'success',
      });

      // Reload batches and auto-select the new one
      await loadBatches();
      if (batchId) {
        setSelectedBatchId(batchId);
      }
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : t('accounting.generate.failed', 'Generation failed'),
        type: 'error',
      });
    } finally {
      setGenerating(false);
      setGeneratingPhase(null);
    }
  };

  // ============================================
  // Concept Toggle
  // ============================================

  const toggleConcept = (concept: AccountingConcept) => {
    setSelectedConcepts(prev => {
      const next = new Set(prev);
      if (next.has(concept)) {
        next.delete(concept);
      } else {
        next.add(concept);
      }
      return next;
    });
  };

  const selectAllConcepts = () => setSelectedConcepts(new Set(ALL_CONCEPTS));
  const deselectAllConcepts = () => setSelectedConcepts(new Set());

  // ============================================
  // Back-dated Generation Logic
  // ============================================

  const toggleBackdateConcept = (concept: AccountingConcept) => {
    setBackdateConcepts(prev => {
      const next = new Set(prev);
      if (next.has(concept)) {
        next.delete(concept);
      } else {
        next.add(concept);
      }
      return next;
    });
  };

  // Compute derived state for backdate modal
  const backdateMaxDate = useMemo(() => {
    if (!systemDateResult.systemDate) return '';
    // System date is YYYY-MM-DD, compute day before
    const d = new Date(systemDateResult.systemDate + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }, [systemDateResult.systemDate]);

  const isPreviousPeriod = useMemo(() => {
    if (!backdateDate || !systemDateResult.systemDate) return false;
    return backdateDate.substring(0, 7) !== systemDateResult.systemDate.substring(0, 7);
  }, [backdateDate, systemDateResult.systemDate]);

  const handleBackdateGenerate = async () => {
    if (!clientId || backdateGenerating || !backdateDate) return;

    if (backdateConcepts.size === 0) {
      setToast({ message: t('accounting.generate.noConceptsSelected', 'Select at least one concept'), type: 'error' });
      return;
    }

    setBackdateGenerating(true);
    setBackdateGeneratingPhase('events');

    // Check for existing batches before generating
    try {
      const batchCheck = await accountingService.getBatches(clientId, 50, backdateDate, backdateDate);
      if (batchCheck.success && batchCheck.data && batchCheck.data.length > 0) {
        setToast({
          message: t('accounting.backdate.existingBatch', 'Accounting has already been generated for this date. Delete the existing batch before re-generating.'),
          type: 'error',
        });
        setBackdateGenerating(false);
        setBackdateGeneratingPhase(null);
        return;
      }
    } catch (err) {
      console.error('Failed to check existing batches:', err);
    }

    let eventsGenerated = 0;
    let entriesGenerated = 0;
    let batchId: string | null = null;

    try {
      // Step 1: Generate events
      const eventsResponse = await accountingService.generate(clientId, {
        dateFrom: backdateDate,
        dateTo: backdateDate,
        concepts: Array.from(backdateConcepts),
        regenerateMode: 'skip_processed',
      });

      if (eventsResponse.success && eventsResponse.data) {
        eventsGenerated = eventsResponse.data.eventsGenerated;
        batchId = eventsResponse.data.batchId;
      } else {
        setToast({ message: eventsResponse.message || t('accounting.generate.failed', 'Generation failed'), type: 'error' });
        setBackdateGenerating(false);
        setBackdateGeneratingPhase(null);
        return;
      }

      // Step 2: Generate entries
      setBackdateGeneratingPhase('entries');

      try {
        const entriesResponse = await accountingService.generateEntries(clientId, {
          batchId: batchId || undefined,
          concepts: Array.from(backdateConcepts),
          dateFrom: backdateDate,
          dateTo: backdateDate,
        });

        if (entriesResponse.success && entriesResponse.data) {
          entriesGenerated = entriesResponse.data.entriesGenerated;
        }
      } catch (entryErr) {
        console.error('Back-dated entry generation failed:', entryErr);
      }

      // Show result
      setToast({
        message: t('accounting.backdate.success', 'Back-dated generation complete: {{events}} events, {{entries}} entries', {
          events: eventsGenerated,
          entries: entriesGenerated,
        }),
        type: 'success',
      });

      // Reload batches and auto-select the new one
      await loadBatches();
      if (batchId) {
        setSelectedBatchId(batchId);
      }

      // Close modal
      setShowBackdateModal(false);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : t('accounting.generate.failed', 'Generation failed'),
        type: 'error',
      });
    } finally {
      setBackdateGenerating(false);
      setBackdateGeneratingPhase(null);
    }
  };

  // ============================================
  // Batch Selection
  // ============================================

  const handleBatchClick = (batch: AccountingBatch) => {
    setSelectedBatchId(batch.id);
  };

  const handleBatchExpandToggle = (e: React.MouseEvent, batch: AccountingBatch) => {
    e.stopPropagation();
    if (batch.runReport) {
      setExpandedBatchId(expandedBatchId === batch.id ? null : batch.id);
    }
  };

  // ============================================
  // Delete Batch
  // ============================================

  const handleDeleteBatch = async (batch: AccountingBatch) => {
    if (!clientId) return;
    setDeletingBatchId(batch.id);
    try {
      const response = await accountingService.deleteBatch(clientId, batch.id);
      if (response.success) {
        const evts = response.data?.eventsDeleted || 0;
        const ents = (response.data as any)?.entriesDeleted || 0;
        setToast({
          message: t('accounting.generate.batchDeleted', 'Batch deleted ({{events}} events, {{entries}} entries removed)', { events: evts, entries: ents }),
          type: 'success',
        });
        if (selectedBatchId === batch.id) {
          setSelectedBatchId(null);
        }
        loadBatches();
      } else {
        setToast({ message: response.message || t('accounting.generate.batchDeleteFailed', 'Failed to delete batch'), type: 'error' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setToast({
        message: msg || t('accounting.generate.batchDeleteFailed', 'Failed to delete batch'),
        type: 'error',
      });
    } finally {
      setDeletingBatchId(null);
      setConfirmDeleteBatch(null);
    }
  };

  // ============================================
  // Delete All Entries (demo mode only)
  // ============================================

  // ============================================
  // Format Helpers
  // ============================================

  const formatDate = (isoDate: string): string => {
    if (!isoDate) return '-';
    const parts = isoDate.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return isoDate;
  };

  const formatDateTime = (isoString: string): string => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
    } catch {
      return isoString;
    }
  };

  const formatAmount = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) return '-';
    return amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  // ============================================
  // AG Grid Row Data
  // ============================================

  const eventsRowData = useMemo(() => events.map(e => {
    const isNotional = e.concept === 'NEW_TRADE' || e.concept === 'EXPIRY' || e.concept === 'MODIFICATION_CANCELLED' || e.concept === 'UNWIND';
    const isValuation = e.concept === 'VALUATION' || e.concept === 'VALUATION_REVERSAL' || e.concept === 'MODIFICATION_CANCELLED';
    const isSettlement = e.concept === 'EXPIRY' || e.concept === 'UNWIND';
    return {
      ...e,
      conceptLabel: `${t(`accounting.concepts.${e.concept}`, CONCEPT_LABELS[e.concept])}${e.leg ? ` (${e.leg})` : ''}`,
      displayCcy1: isNotional ? (e.currency1 || '') : '',
      displayNotional1: isNotional ? (e.amount1 ?? null) : null,
      displayCcy2: isNotional ? (e.currency2 || '') : '',
      displayNotional2: isNotional ? (e.amount2 ?? null) : null,
      displaySettCcy: isSettlement ? (e.settlementCurrency || '') : '',
      displaySettlement: isSettlement ? (e.settlementAmount ?? null) : null,
      displayMtm: isValuation ? (e.valuationAmount ?? null) : null,
      displaySpotElement: isValuation ? ((e as any).spotElement ?? null) : null,
      displayForwardElement: isValuation ? ((e as any).forwardElement ?? null) : null,
    };
  }), [events, t]);

  const entriesRowData = useMemo(() => {
    const filtered = selectedEventIds.size > 0
      ? entries.filter(e => selectedEventIds.has(e.eventId))
      : entries;
    return filtered.map(e => ({
      ...e,
      conceptLabel: t(`accounting.concepts.${e.concept}`, CONCEPT_LABELS[e.concept] || e.concept),
      ruleName: rulesMap[e.ruleId]?.name || e.ruleId.slice(0, 8),
      ruleDescription: rulesMap[e.ruleId]?.description || '',
    }));
  }, [entries, rulesMap, t, selectedEventIds]);

  // ============================================
  // Entries Totals
  // ============================================

  const [entriesTotals, setEntriesTotals] = useState({ totalDebit: 0, totalCredit: 0, balance: 0 });
  const [entriesVisibleCount, setEntriesVisibleCount] = useState(0);

  useEffect(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    for (const entry of entriesRowData) {
      totalDebit += entry.debitAmount || 0;
      totalCredit += entry.creditAmount || 0;
    }
    setEntriesTotals({ totalDebit, totalCredit, balance: totalDebit - totalCredit });
    setEntriesVisibleCount(entriesRowData.length);
  }, [entriesRowData]);

  const handleEntriesFilterChanged = useCallback((event: FilterChangedEvent) => {
    let count = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    event.api.forEachNodeAfterFilter((node: any) => {
      if (node.data) {
        count++;
        totalDebit += node.data.debitAmount || 0;
        totalCredit += node.data.creditAmount || 0;
      }
    });
    setEntriesVisibleCount(count);
    setEntriesTotals({ totalDebit, totalCredit, balance: totalDebit - totalCredit });
  }, []);

  // ============================================
  // AG Grid Column Definitions
  // ============================================

  const conceptBadgeRenderer = (p: any) => {
    if (!p.value) return null;
    return (
      <span style={{
        padding: '0.1rem 0.45rem',
        borderRadius: '4px',
        fontSize: '0.78rem',
        background: 'rgba(52,152,219,0.15)',
        color: 'var(--accent-blue)',
        whiteSpace: 'nowrap',
      }}>
        {p.value}
      </span>
    );
  };

  const eventColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: t('accounting.generate.eventDate', 'Date'),
      field: 'eventDate',
      sortable: true,
      filter: true,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: t('accounting.generate.eventConcept', 'Concept'),
      field: 'conceptLabel',
      sortable: true,
      filter: true,
      cellRenderer: conceptBadgeRenderer,
    },
    {
      headerName: t('accounting.generate.eventTrade', 'Trade #'),
      field: 'tradeNumber',
      sortable: true,
      filter: true,
      sort: 'asc' as const,
      cellStyle: { fontFamily: 'monospace', fontWeight: '500' },
      comparator: (a: string, b: string) => {
        // Extract base trade number (e.g. '12345' from 'INT-12345.1.1' or 'SPL-12345.2')
        const baseNum = (s: string) => {
          const m = s?.match(/(?:INT-|SPL-)?(\d+)/);
          return m ? parseInt(m[1], 10) : 0;
        };
        const aBase = baseNum(a);
        const bBase = baseNum(b);
        if (aBase !== bBase) return aBase - bBase;
        // Same base: parent first, then children sorted by full string
        const aIsChild = a?.startsWith('INT-') || a?.startsWith('SPL-');
        const bIsChild = b?.startsWith('INT-') || b?.startsWith('SPL-');
        if (!aIsChild && bIsChild) return -1;
        if (aIsChild && !bIsChild) return 1;
        return (a || '').localeCompare(b || '');
      },
    },
    {
      headerName: t('accounting.generate.eventProduct', 'Product'),
      field: 'productType',
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        if (params.value === 'Forward') {
          if (params.data?.isForwardStarting) return 'Forward (Starting)';
          if (params.data?.isAverageRateForward) return 'Forward (Asiático)';
        }
        return params.value;
      },
    },
    {
      headerName: t('accounting.generate.eventDirection', 'Dir'),
      field: 'direction',
      minWidth: 60,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.generate.eventCcy1', 'CCY1'),
      field: 'displayCcy1',
      minWidth: 62,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.generate.eventNotional1', 'Notional 1'),
      field: 'displayNotional1',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.generate.eventCcy2', 'CCY2'),
      field: 'displayCcy2',
      minWidth: 62,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.generate.eventNotional2', 'Notional 2'),
      field: 'displayNotional2',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.generate.eventSettCcy', 'Sett CCY'),
      field: 'displaySettCcy',
      minWidth: 62,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.generate.eventSettlement', 'Settlement'),
      field: 'displaySettlement',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.generate.eventMtm', 'MtM'),
      field: 'displayMtm',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.generate.eventSpotElement', 'Spot Element'),
      field: 'displaySpotElement',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.generate.eventForwardElement', 'Fwd Element'),
      field: 'displayForwardElement',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.generate.eventProcessed', 'Done'),
      field: 'processed',
      minWidth: 60,
      sortable: true,
      filter: true,
      cellRenderer: (p: any) => p.value
        ? <span style={{ color: '#2ecc71' }}>&#10003;</span>
        : <span style={{ color: 'var(--text-secondary)' }}>&#8212;</span>,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const entryColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: t('accounting.entries.colDate', 'Date'),
      field: 'entryDate',
      sortable: true,
      filter: true,
      valueFormatter: (p) => formatDate(p.value),
    },
    {
      headerName: t('accounting.entries.colConcept', 'Concept'),
      field: 'conceptLabel',
      sortable: true,
      filter: true,
      cellRenderer: conceptBadgeRenderer,
    },
    {
      headerName: t('accounting.entries.colTrade', 'Trade #'),
      field: 'tradeNumber',
      sortable: true,
      filter: true,
      sort: 'asc' as const,
      cellStyle: { fontFamily: 'monospace', fontWeight: '500' },
      comparator: (a: string, b: string) => {
        const baseNum = (s: string) => {
          const m = s?.match(/(?:INT-|SPL-)?(\d+)/);
          return m ? parseInt(m[1], 10) : 0;
        };
        const aBase = baseNum(a);
        const bBase = baseNum(b);
        if (aBase !== bBase) return aBase - bBase;
        const aIsChild = a?.startsWith('INT-') || a?.startsWith('SPL-');
        const bIsChild = b?.startsWith('INT-') || b?.startsWith('SPL-');
        if (!aIsChild && bIsChild) return -1;
        if (aIsChild && !bIsChild) return 1;
        return (a || '').localeCompare(b || '');
      },
    },
    {
      headerName: t('accounting.entries.colAccount', 'Account'),
      field: 'accountNumber',
      sortable: true,
      filter: true,
      cellStyle: { fontFamily: 'monospace' },
    },
    {
      headerName: t('accounting.entries.colAccountName', 'Account Name'),
      field: 'accountName',
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.entries.colCostCentre', 'Cost Centre'),
      field: 'costCentre',
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.entries.colDebit', 'Debit'),
      field: 'debitAmount',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.entries.colCredit', 'Credit'),
      field: 'creditAmount',
      sortable: true,
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: (p) => p.value ? formatAmount(p.value) : '',
      cellStyle: { fontFamily: 'monospace', textAlign: 'right' },
    },
    {
      headerName: t('accounting.entries.colCurrency', 'CCY'),
      field: 'currency',
      minWidth: 62,
      sortable: true,
      filter: true,
    },
    {
      headerName: t('accounting.entries.colRule', 'Rule'),
      field: 'ruleName',
      sortable: true,
      filter: true,
      tooltipField: 'ruleDescription',
      cellStyle: { fontSize: '0.8rem', color: 'var(--text-secondary)' },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // ============================================
  // Access Check
  // ============================================

  if (!canView) {
    return (
      <div className="accounting-page">
        <div className="access-denied">
          <p>{t('common.accessDenied')}</p>
        </div>
      </div>
    );
  }

  // ============================================
  // Main Render
  // ============================================

  return (
    <div className="accounting-page">
      {/* Header with generation controls */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>{t('accounting.title')}</h1>
        <div className="accounting-header-controls">
          {/* Mode */}
          <select
            value={regenerateMode}
            onChange={(e) => setRegenerateMode(e.target.value as any)}
            style={{
              height: '32px',
              padding: '0 0.5rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              color: 'var(--text-primary)',
              fontSize: '0.82rem',
              cursor: 'pointer',
            }}
          >
            <option value="skip_processed">{t('accounting.generate.modeSkip', 'Skip existing')}</option>
            <option value="delete_and_regenerate">{t('accounting.generate.modeRegenerate', 'Delete & regenerate')}</option>
          </select>

          {/* Concept toggle buttons */}
          {ALL_CONCEPTS.map((concept) => (
            <button
              key={concept}
              onClick={() => toggleConcept(concept)}
              style={{
                padding: '0 0.5rem',
                height: '32px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                backgroundColor: selectedConcepts.has(concept) ? '#007bff' : 'var(--bg-secondary)',
                color: '#fff',
                fontSize: '11px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                lineHeight: '30px',
                whiteSpace: 'nowrap',
              }}
            >
              {t(`accounting.concepts.${concept}`, CONCEPT_LABELS[concept])}
            </button>
          ))}

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={generating || !canManage || selectedConcepts.size === 0 || !systemDateResult.systemDate}
            style={{
              background: '#2ecc71',
              color: '#fff',
              border: 'none',
              padding: '0 1rem',
              height: '32px',
              borderRadius: '4px',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: (generating || !canManage || selectedConcepts.size === 0 || !systemDateResult.systemDate) ? 'not-allowed' : 'pointer',
              opacity: (generating || !canManage || selectedConcepts.size === 0 || !systemDateResult.systemDate) ? 0.6 : 1,
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
            }}
          >
            {generating
              ? (generatingPhase === 'entries'
                ? t('accounting.generate.generatingEntries', 'Generating entries...')
                : t('accounting.generate.generatingEvents', 'Generating events...'))
              : t('accounting.generate.buttonCombined', 'Generate Accounting')}
          </button>

          {/* Back-dated generation icon button */}
          <button
            onClick={() => {
              setBackdateDate('');
              setBackdateConcepts(new Set(BACKDATE_CONCEPTS));
              setShowBackdateModal(true);
            }}
            disabled={!canManage || !systemDateResult.systemDate}
            title={t('accounting.backdate.buttonTooltip', 'Back-dated generation')}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              fontSize: '1rem',
              cursor: (!canManage || !systemDateResult.systemDate) ? 'not-allowed' : 'pointer',
              opacity: (!canManage || !systemDateResult.systemDate) ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
          >
            <History size={16} />
          </button>

          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
        </div>
      </div>

      {/* Resizable Panel Layout */}
      <PanelGroup orientation="vertical" style={{ flex: 1, minHeight: 0 }}>
        {/* Top Panel — Batch History */}
        <Panel defaultSize={35} minSize={15}>
          <div className="config-section" style={{ height: '100%', overflow: 'auto', marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('accounting.generate.recentBatches', 'Recent Batches')}
            </h3>
            <span style={{
              backgroundColor: 'var(--bg-tertiary)',
              padding: '0.1rem 0.5rem',
              borderRadius: '10px',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
            }}>
              {batches.length}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem' }}>
            <input
              type="date"
              value={pendingBatchDateFrom}
              onChange={(e) => setPendingBatchDateFrom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setBatchDateFrom(pendingBatchDateFrom); setBatchDateTo(pendingBatchDateTo); } }}
              style={{
                height: '28px', padding: '0 0.4rem',
                background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                borderRadius: '4px', color: 'var(--text-primary)', fontSize: '0.8rem',
              }}
            />
            <span style={{ color: 'var(--text-secondary)' }}>–</span>
            <input
              type="date"
              value={pendingBatchDateTo}
              onChange={(e) => setPendingBatchDateTo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setBatchDateFrom(pendingBatchDateFrom); setBatchDateTo(pendingBatchDateTo); } }}
              style={{
                height: '28px', padding: '0 0.4rem',
                background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                borderRadius: '4px', color: 'var(--text-primary)', fontSize: '0.8rem',
              }}
            />
            {(pendingBatchDateFrom !== batchDateFrom || pendingBatchDateTo !== batchDateTo) && (pendingBatchDateFrom || pendingBatchDateTo) && (
              <button
                onClick={() => { setBatchDateFrom(pendingBatchDateFrom); setBatchDateTo(pendingBatchDateTo); }}
                style={{
                  height: '28px', padding: '0 0.5rem',
                  background: 'rgba(255,255,255,0.1)', border: '1px solid var(--border-color)',
                  borderRadius: '4px', color: 'var(--text-primary)', fontSize: '0.8rem',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                OK
              </button>
            )}
            {(batchDateFrom || batchDateTo) && (
              <button
                onClick={() => { setBatchDateFrom(''); setBatchDateTo(''); setPendingBatchDateFrom(''); setPendingBatchDateTo(''); }}
                style={{
                  height: '28px', padding: '0 0.5rem',
                  background: 'transparent', border: '1px solid var(--border-color)',
                  borderRadius: '4px', color: 'var(--text-secondary)', fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                {t('common.clear', 'Clear')}
              </button>
            )}
          </div>
        </div>

        {batches.length > 0 ? (
          <div className="accounts-table-container">
            <table className="accounts-table">
              <thead>
                <tr>
                  <th>{t('accounting.generate.eventBatch', 'Batch')}</th>
                  <th>{t('accounting.generate.batchDate', 'Date')}</th>
                  <th>{t('accounting.generate.batchTrigger', 'Trigger')}</th>
                  <th>{t('accounting.generate.batchConcepts', 'Concepts')}</th>
                  <th>{t('accounting.generate.batchEvents', 'Events')}</th>
                  <th>{t('accounting.generate.batchEntries', 'Entries')}</th>
                  <th>{t('accounting.generate.batchStatus', 'Status')}</th>
                  <th>{t('accounting.generate.batchDateRange', 'Date Range')}</th>
                  {canManage && <th style={{ width: '60px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <React.Fragment key={batch.id}>
                    <tr
                      className={selectedBatchId === batch.id ? 'batch-row-selected' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleBatchClick(batch)}
                    >
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {batch.runReport && (
                          <span
                            onClick={(e) => handleBatchExpandToggle(e, batch)}
                            style={{ marginRight: '0.3rem', fontSize: '0.7rem', color: 'var(--text-secondary)', cursor: 'pointer' }}
                          >
                            {expandedBatchId === batch.id ? '\u25BC' : '\u25B6'}
                          </span>
                        )}
                        {batch.id ? batch.id.slice(0, 8) : '-'}
                      </td>
                      <td>{formatDateTime(batch.generatedAt)}</td>
                      <td>
                        {(() => {
                          const source = batch.triggerSource || 'manual';
                          const isScheduled = source.startsWith('scheduled_');
                          return (
                            <span style={{
                              display: 'inline-block',
                              padding: '0.15rem 0.45rem',
                              borderRadius: '4px',
                              fontSize: '0.73rem',
                              fontWeight: 500,
                              backgroundColor: isScheduled ? 'rgba(155, 89, 182, 0.2)' : 'rgba(52, 152, 219, 0.15)',
                              color: isScheduled ? '#9b59b6' : 'var(--accent-blue)',
                            }}>
                              {source === 'scheduled_daily'
                                ? t('accounting.generate.triggerScheduledDaily', 'Scheduled (Daily)')
                                : source === 'scheduled_monthly'
                                  ? t('accounting.generate.triggerScheduledMonthly', 'Scheduled (Monthly)')
                                  : t('accounting.generate.triggerManual', 'Manual')}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>
                        {(batch.concepts || []).map(c => t(`accounting.concepts.${c}`, c)).join(', ')}
                      </td>
                      <td>{batch.eventsGenerated}</td>
                      <td>{batch.entriesGenerated || 0}</td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: batch.status === 'completed'
                            ? 'rgba(46, 204, 113, 0.2)'
                            : batch.status === 'completed_with_errors'
                              ? 'rgba(255, 193, 7, 0.2)'
                              : 'rgba(231, 76, 60, 0.2)',
                          color: batch.status === 'completed'
                            ? '#2ecc71'
                            : batch.status === 'completed_with_errors'
                              ? '#f39c12'
                              : '#e74c3c',
                        }}>
                          {batch.status === 'completed'
                            ? t('accounting.generate.statusCompleted', 'Completed')
                            : batch.status === 'completed_with_errors'
                              ? t('accounting.generate.statusPartial', 'Partial')
                              : t('accounting.generate.statusFailed', 'Failed')}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {batch.dateFrom && batch.dateTo && batch.dateFrom === batch.dateTo
                          ? formatDate(batch.dateFrom)
                          : <>
                              {batch.dateFrom ? formatDate(batch.dateFrom) : '*'}
                              {' \u2013 '}
                              {batch.dateTo ? formatDate(batch.dateTo) : '*'}
                            </>
                        }
                      </td>
                      {canManage && (
                        <td>
                          <button
                            className="btn-icon btn-danger"
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteBatch(batch); }}
                            disabled={deletingBatchId === batch.id}
                            title={t('accounting.generate.deleteBatch', 'Delete batch')}
                            style={{ fontSize: '0.8rem' }}
                          >
                            {deletingBatchId === batch.id ? '...' : '\u2715'}
                          </button>
                        </td>
                      )}
                    </tr>
                    {/* Expandable Run Report */}
                    {expandedBatchId === batch.id && batch.runReport && (
                      <tr>
                        <td colSpan={canManage ? 9 : 8} style={{ padding: 0, borderTop: 'none' }}>
                          <div style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: 'var(--bg-secondary)',
                            borderTop: '1px solid var(--border-color)',
                            fontSize: '0.85rem',
                          }}>
                            <div style={{ fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                              {t('accounting.generate.runReportTitle', 'Run Report')}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.4rem 1.5rem', color: 'var(--text-secondary)' }}>
                              <div>
                                <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportMode', 'Mode')}:</span>{' '}
                                {batch.runReport.mode === 'daily'
                                  ? t('accounting.generate.runReportModeDaily', 'Daily')
                                  : t('accounting.generate.runReportModeMonthly', 'Monthly')}
                              </div>
                              <div>
                                <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportSystemDate', 'System Date')}:</span>{' '}
                                {formatDate(batch.runReport.systemDate)}
                              </div>
                              <div>
                                <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportDateRange', 'Date Range')}:</span>{' '}
                                {formatDate(batch.runReport.dateFrom)} {'\u2013'} {formatDate(batch.runReport.dateTo)}
                              </div>
                              <div>
                                <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportEvents', 'Events Generated')}:</span>{' '}
                                {batch.runReport.eventsGenerated}
                              </div>
                              <div>
                                <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportEntries', 'Entries Generated')}:</span>{' '}
                                {batch.runReport.entriesGenerated}
                              </div>
                              {(batch.runReport.eventsErrors > 0 || batch.runReport.entriesErrors > 0) && (
                                <div style={{ color: '#e74c3c' }}>
                                  <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportErrors', 'Errors')}:</span>{' '}
                                  {batch.runReport.eventsErrors} {t('accounting.generate.runReportEventsLabel', 'events')},{' '}
                                  {batch.runReport.entriesErrors} {t('accounting.generate.runReportEntriesLabel', 'entries')}
                                </div>
                              )}
                              <div>
                                <span style={{ fontWeight: 500 }}>{t('accounting.generate.runReportConcepts', 'Concepts')}:</span>{' '}
                                {(batch.runReport.concepts || []).map(c => t(`accounting.concepts.${c}`, c)).join(', ')}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0 0 0' }}>
            {t('accounting.generate.noBatches', 'No generation batches yet.')}
          </p>
        )}
          </div>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* Bottom Panel — Detail Grids */}
        <Panel defaultSize={65} minSize={25}>
      {/* Detail Grids: Events (left) + Entries (right) */}
      {selectedBatchId ? (
        <div className="accounting-detail-grid">
          {/* Events Grid */}
          <div className="accounting-detail-panel config-section" style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                {t('accounting.generate.eventsTitle', 'Events')}
                <span style={{
                  marginLeft: '0.5rem',
                  backgroundColor: 'var(--bg-tertiary)',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '10px',
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                  fontWeight: 400,
                }}>
                  {eventsRowData.length}
                </span>
              </h3>
              <button
                onClick={() => selectedBatchId && loadEventsForBatch(selectedBatchId)}
                disabled={eventsLoading}
                className="btn-secondary btn-small"
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                {eventsLoading && <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2, margin: 0 }} />}
                {t('common.refresh', 'Refresh')}
              </button>
            </div>

            {eventsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '0.75rem', flex: 1 }}>
                <div style={{
                  width: 24, height: 24,
                  border: '2px solid var(--border-color)',
                  borderTopColor: 'var(--accent-blue)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('common.loading', 'Loading...')}</span>
              </div>
            ) : eventsRowData.length === 0 ? (
              <div className="empty-message" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {t('accounting.generate.noEventsForBatch', 'No events in this batch.')}
              </div>
            ) : (
              <div ref={eventsContainerRef} className="ag-theme-alpine-dark trade-grid" style={{ flex: 1, minHeight: 0 }}>
                <AgGridReact
                  localeText={agGridLocale}
                  ref={eventsGridRef}
                  rowData={eventsRowData}
                  columnDefs={eventColumnDefs}
                  defaultColDef={{ resizable: true, sortable: true, filter: true }}
                  domLayout="normal"
                  rowSelection="multiple"
                  onSelectionChanged={(e) => {
                    const selected = e.api.getSelectedRows();
                    setSelectedEventIds(new Set(selected.map((r: any) => r.id)));
                  }}
                  onFirstDataRendered={(p) => p.api.autoSizeAllColumns()}
                />
              </div>
            )}
            {showEventMenu && (
              <InlineMenu items={eventMenuItems} position={eventMenuPosition} onClose={closeEventMenu} />
            )}
          </div>

          {/* Entries Grid */}
          <div className="accounting-detail-panel config-section" style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {t('accounting.entries.title', 'Entries')}
                <span style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  padding: '0.1rem 0.5rem',
                  borderRadius: '10px',
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)',
                  fontWeight: 400,
                }}>
                  {entriesRowData.length}
                </span>
                {selectedEventIds.size > 0 && (
                  <button
                    onClick={() => {
                      setSelectedEventIds(new Set());
                      eventsGridRef.current?.api?.deselectAll();
                    }}
                    className="btn-secondary btn-small"
                    style={{ fontSize: '0.75rem', padding: '0.1rem 0.5rem' }}
                  >
                    {t('accounting.entries.showAll', 'Show All')}
                  </button>
                )}
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  onClick={() => setShowTemplatePicker(true)}
                  disabled={entries.length === 0}
                  className="btn-secondary btn-small"
                >
                  {t('accounting.entries.download', 'Download')}
                </button>
                <button
                  onClick={() => selectedBatchId && loadEntriesForBatch(selectedBatchId)}
                  disabled={entriesLoading}
                  className="btn-secondary btn-small"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                >
                  {entriesLoading && <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2, margin: 0 }} />}
                  {t('common.refresh', 'Refresh')}
                </button>
              </div>
            </div>

            {entriesLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '0.75rem', flex: 1 }}>
                <div style={{
                  width: 24, height: 24,
                  border: '2px solid var(--border-color)',
                  borderTopColor: 'var(--accent-blue)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{t('common.loading', 'Loading...')}</span>
              </div>
            ) : entriesRowData.length === 0 ? (
              <div className="empty-message" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {t('accounting.entries.noEntriesForBatch', 'No entries in this batch.')}
              </div>
            ) : (
              <>
                <div ref={entriesContainerRef} className="ag-theme-alpine-dark trade-grid" style={{ flex: 1, minHeight: 0 }}>
                  <AgGridReact
                    localeText={agGridLocale}
                    ref={entriesGridRef}
                    rowData={entriesRowData}
                    columnDefs={entryColumnDefs}
                    defaultColDef={{ resizable: true, sortable: true, filter: true }}
                    domLayout="normal"
                    onFirstDataRendered={(p) => p.api.autoSizeAllColumns()}
                    onFilterChanged={handleEntriesFilterChanged}
                  />
                </div>
                <div className="table-footer">
                  <span>{t('accounting.entries.showingEntries', 'Showing {{count}} entries', { count: entriesVisibleCount })}</span>
                  {entriesVisibleCount > 0 && (
                    <span style={{ marginLeft: '2rem', fontSize: '0.85rem' }}>
                      {t('accounting.entries.totalDebit', 'Total Debit')}: <strong>{formatAmount(entriesTotals.totalDebit)}</strong>
                      {' | '}
                      {t('accounting.entries.totalCredit', 'Total Credit')}: <strong>{formatAmount(entriesTotals.totalCredit)}</strong>
                      {' | '}
                      {t('accounting.entries.balance', 'Balance')}:{' '}
                      <strong style={{ color: Math.abs(entriesTotals.balance) < 0.01 ? '#2ecc71' : '#e74c3c' }}>
                        {formatAmount(entriesTotals.balance)}
                      </strong>
                    </span>
                  )}
                </div>
              </>
            )}
            {showEntryMenu && (
              <InlineMenu items={entryMenuItems} position={entryMenuPosition} onClose={closeEntryMenu} />
            )}
          </div>
        </div>
      ) : (
        <div className="config-section" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="empty-message">
            {t('accounting.generate.selectBatch', 'Select a batch above to view events and entries.')}
          </div>
        </div>
      )}
        </Panel>
      </PanelGroup>

      {/* Toast */}
      <Toast
        message={toast?.message || ''}
        type={toast?.type}
        isVisible={!!toast}
        onClose={() => setToast(null)}
      />

      {/* Alert */}
      <AlertModal
        isOpen={!!alert}
        title={alert?.title || ''}
        message={alert?.message || ''}
        type={alert?.type}
        onClose={() => setAlert(null)}
      />

      {/* Delete Batch Confirmation */}
      {confirmDeleteBatch && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteBatch(null)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('accounting.generate.confirmDeleteTitle', 'Delete Batch')}</h2>
              <button className="close-button" onClick={() => setConfirmDeleteBatch(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 0.75rem 0', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                {t('accounting.generate.confirmDeleteMessage', 'This will permanently delete this batch and all associated events and entries.', { events: confirmDeleteBatch.eventsGenerated, entries: confirmDeleteBatch.entriesGenerated || 0 })}
              </p>
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '6px',
                padding: '0.75rem',
                fontSize: '0.85rem',
                color: 'var(--text-secondary)',
              }}>
                <div><strong>{t('accounting.generate.eventBatch', 'Batch')}:</strong> {confirmDeleteBatch.id.slice(0, 8)}</div>
                <div><strong>{t('accounting.generate.batchDate', 'Date')}:</strong> {formatDateTime(confirmDeleteBatch.generatedAt)}</div>
                <div><strong>{t('accounting.generate.batchEvents', 'Events')}:</strong> {confirmDeleteBatch.eventsGenerated}</div>
                <div><strong>{t('accounting.generate.batchEntries', 'Entries')}:</strong> {confirmDeleteBatch.entriesGenerated || 0}</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setConfirmDeleteBatch(null)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-primary btn-danger-bg"
                onClick={() => handleDeleteBatch(confirmDeleteBatch)}
                disabled={deletingBatchId === confirmDeleteBatch.id}
              >
                {deletingBatchId === confirmDeleteBatch.id
                  ? t('common.deleting', 'Deleting...')
                  : t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back-dated Generation Modal */}
      {showBackdateModal && (
        <div className="modal-overlay" onClick={() => !backdateGenerating && setShowBackdateModal(false)}>
          <div className="modal-content" style={{ maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('accounting.backdate.modalTitle', 'Back-dated Accounting Generation')}</h2>
              <button className="close-button" onClick={() => !backdateGenerating && setShowBackdateModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Date picker */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>{t('accounting.backdate.dateLabel', 'Accounting Date')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="date"
                    value={backdateDate}
                    onChange={(e) => setBackdateDate(e.target.value)}
                    max={backdateMaxDate}
                    style={{
                      height: '32px',
                      padding: '0 0.5rem',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem',
                      flex: 1,
                    }}
                  />
                </div>
              </div>

              {/* Concept toggles */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label>{t('accounting.backdate.concepts', 'Concepts')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {BACKDATE_CONCEPTS.map((concept) => (
                    <button
                      key={concept}
                      onClick={() => toggleBackdateConcept(concept)}
                      style={{
                        padding: '0 0.5rem',
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: backdateConcepts.has(concept) ? '#007bff' : 'var(--bg-secondary)',
                        color: '#fff',
                        fontSize: '11px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        lineHeight: '30px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t(`accounting.concepts.${concept}`, CONCEPT_LABELS[concept])}
                    </button>
                  ))}
                </div>
              </div>

              {/* Previous period warning */}
              {isPreviousPeriod && backdateDate && (
                <div className="backdate-warning-amber">
                  {t('accounting.backdate.previousPeriod', 'The selected date is in a previous accounting period (month). Ensure this is intentional.')}
                </div>
              )}

              {/* Disclaimer note */}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.4, marginTop: '0.5rem' }}>
                <strong>{t('accounting.backdate.note', 'Note')}:</strong>{' '}
                {t('accounting.backdate.disclaimer', 'Uses current trade workflow state, not the state as of the selected date.')}{' '}
                {t('accounting.backdate.nonBusinessDay', 'If the selected date is not a business day, no events will be generated.')}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowBackdateModal(false)}
                disabled={backdateGenerating}
                style={{ height: '32px', padding: '0 1rem', fontSize: '0.85rem' }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleBackdateGenerate}
                disabled={backdateGenerating || !backdateDate || backdateConcepts.size === 0}
                style={{
                  background: '#f39c12',
                  color: '#fff',
                  border: 'none',
                  padding: '0 1rem',
                  height: '32px',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: (backdateGenerating || !backdateDate || backdateConcepts.size === 0) ? 'not-allowed' : 'pointer',
                  opacity: (backdateGenerating || !backdateDate || backdateConcepts.size === 0) ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                }}
              >
                {backdateGenerating
                  ? t('accounting.backdate.generating', 'Generating...')
                  : t('accounting.backdate.generate', 'Generate (back-dated)')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Entries Confirmation Modal (demo mode) */}
      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <TemplatePicker
          clientId={clientId}
          sourceType="ACCOUNTING_ENTRIES"
          params={{ batchId: selectedBatchId || undefined }}
          onClose={() => setShowTemplatePicker(false)}
          onSuccess={(msg) => setToast({ message: msg, type: 'success' })}
          onError={(msg) => setToast({ message: msg, type: 'error' })}
        />
      )}
    </div>
  );
};

export default Accounting;
